import { ComponentProps, memo, SyntheticEvent } from 'react';

import { UnFieldArray } from 'components';

import { useOperationOptions } from 'hooks/useOperationOptions';

import { debounce } from 'utils';
import { isOdd } from 'utils/help';

import * as Styles from './styles';

import { SimpleOperationList } from 'types/operation';

interface OperationArrayProps
  extends Pick<
    ComponentProps<typeof UnFieldArray>,
    'label' | 'disabled' | 'value' | 'onChangeValue' | 'onSelect' | 'onGetValue'
  > {
  name: string;
  status?: boolean;
  onValidate?: (operations: SimpleOperationList) => Promise<void>;
  onRemoveRow?: (operation: SimpleOperationList) => void;
}

function BaseOperationArray({
  onValidate,
  onRemoveRow,
  onChangeValue,
  status,
  ...props
}: OperationArrayProps) {
  const { operations, onLoadOperations } = useOperationOptions();

  const handleChange = (event: SyntheticEvent<Element, Event>, value: string) => {
    if (!value) return;

    onLoadOperations({
      search: value,
      status: typeof status === 'boolean' ? (status ? 1 : 0) : undefined,
    });
  };

  return (
    <UnFieldArray
      optionLabel="name"
      optionValue="id"
      options={operations}
      onInputChange={debounce(handleChange)}
      onValidate={onValidate}
      onChangeValue={onChangeValue}
      unicKeyFilter="id"
      {...props}
      render={({ remove }, _operations) =>
        _operations.map((operation: SimpleOperationList, index) => (
          <Styles.Item isOdd={isOdd(index)} key={index}>
            <span>{operation.name}</span>
            <Styles.RemoveButton
              onClick={() => {
                onRemoveRow?.(operation);
                remove(index);
              }}
              icon={{
                name: 'close',
                color: 'text100',
              }}
            />
          </Styles.Item>
        ))
      }
    />
  );
}

export const UnOperationArray = memo(BaseOperationArray);
